import { useCallback, useEffect, useState } from "react";
import {
  applyResolvedTheme,
  readStoredPreference,
  resolveTheme,
  systemPrefersDark,
  THEME_STORAGE_KEY,
  type ThemePreference,
} from "@/lib/theme";

/**
 * CIR-2026-361 — light/dark theme preference.
 *
 * Manages the "system" | "light" | "dark" preference: persists to localStorage
 * (key `spire-theme`), toggles `.dark` on <html> immediately on change (no
 * reload), and — while the preference is "system" — follows live OS scheme
 * changes. The pre-paint bootstrap (public/theme-init.js) sets the initial class
 * before React mounts; this hook keeps it in sync thereafter.
 */
export function useTheme() {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    readStoredPreference(),
  );

  // Apply the resolved theme whenever the preference changes.
  useEffect(() => {
    applyResolvedTheme(resolveTheme(preference, systemPrefersDark()));
  }, [preference]);

  // While "system", follow live OS colour-scheme changes.
  useEffect(() => {
    if (preference !== "system") return;
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => {
      applyResolvedTheme(e.matches ? "dark" : "light");
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Storage unavailable (private mode / quota) — preference stays in-memory.
    }
  }, []);

  return { preference, setPreference } as const;
}
